/**
 * F44 (Wave 2D) — admin finance refunds page.
 *
 * Route: `/finance/refunds`. Permission: `financial:read`.
 *
 * A read-only paginated Table over GET /admin/financial/refunds — the
 * consolidated refunds/reversals UNION across booking_refund, commission_reversal,
 * placement_credit, promotion_reversal, tournament_refund — with a source filter
 * + date range. Each row deep-links into the owning admin surface via
 * `deepLinkPath`. F44 writes nothing; the actual refund happens in the owner.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  CurrencyDisplay,
  DateRangePicker,
  EmptyState,
  Pagination,
  Select,
  Skeleton,
  Table,
  type TableColumn,
} from '@bravemove/ui-web';
import {
  REFUND_SOURCE_KINDS,
  type FinanceRefundRow,
  type RefundSourceKind,
} from '@bravemove/types';
import { adminFinancialApi, type RefundsOpts } from '../../api/financial';
import { PageHeader } from '../../components/PageHeader';
import { defaultFinanceRange, formatShortDate } from '../../components/finance/financeHelpers';
import { FinanceNav } from './FinanceNav';

const PAGE_SIZE = 25;

export function FinanceRefundsPage() {
  const { t, locale } = useI18n();

  const defaultRange = useMemo(() => defaultFinanceRange(), []);
  const [from, setFrom] = useState(defaultRange.from);
  const [to, setTo] = useState(defaultRange.to);
  const [source, setSource] = useState<RefundSourceKind | ''>('');
  const [page, setPage] = useState(1);

  const [rows, setRows] = useState<readonly FinanceRefundRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const opts: RefundsOpts = {
        from,
        to,
        page,
        perPage: PAGE_SIZE,
        ...(source ? { source } : {}),
      };
      const data = await adminFinancialApi.listRefunds(opts);
      setRows(data.data);
      setTotal(data.pagination.total);
    } catch {
      setError(t('admin:financial.errors.generic'));
    } finally {
      setLoading(false);
    }
  }, [from, to, source, page, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchList();
  }, [fetchList]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const columns = useMemo<readonly TableColumn<FinanceRefundRow>[]>(
    () => [
      {
        key: 'occurredAt',
        header: t('admin:financial.refunds.column.occurredAt'),
        sortable: true,
        sortAccessor: (row) => row.occurredAt,
        cell: (row) => (
          <span className="text-[12.5px] text-ink">{formatShortDate(row.occurredAt, locale)}</span>
        ),
      },
      {
        key: 'source',
        header: t('admin:financial.refunds.column.source'),
        cell: (row) => (
          <span className="font-mono text-[11px] uppercase tracking-wide text-ink-2">
            {t(`admin:financial.refunds.source.${row.source}`, { defaultValue: row.source })}
          </span>
        ),
      },
      {
        key: 'entity',
        header: t('admin:financial.refunds.column.entity'),
        cell: (row) => <span className="text-[13px] font-semibold text-ink">{row.entityName}</span>,
      },
      {
        key: 'originalInvoice',
        header: t('admin:financial.refunds.column.originalInvoice'),
        cell: (row) => (
          <span className="font-mono text-[11px] text-ink-3">{row.originalInvoiceId ?? '—'}</span>
        ),
      },
      {
        key: 'amount',
        header: t('admin:financial.refunds.column.amount'),
        align: 'end',
        sortable: true,
        sortAccessor: (row) => row.amountMinor,
        cell: (row) => (
          <CurrencyDisplay amount={row.amountMinor} currency={row.currency} locale={locale} />
        ),
      },
      {
        key: 'status',
        header: t('admin:financial.refunds.column.status'),
        cell: (row) => <span className="text-[12px] text-ink-2">{row.status}</span>,
      },
      {
        key: 'actions',
        header: t('admin:financial.refunds.column.actions'),
        align: 'end',
        cell: (row) => (
          <Link
            to={row.deepLinkPath}
            className="text-[12px] font-semibold text-accent hover:underline"
            data-testid={`finance-refund-open-${row.id}`}
          >
            {t('admin:financial.refunds.open')}
          </Link>
        ),
      },
    ],
    [t, locale],
  );

  return (
    <div className="space-y-6" data-testid="finance-refunds-page">
      <PageHeader
        eyebrow="Finance"
        title={t('admin:financial.refunds.title')}
        subtitle={t('admin:financial.refunds.subtitle')}
      />
      <FinanceNav />

      <div className="flex flex-wrap items-end gap-3" data-testid="finance-refunds-filters">
        <div className="min-w-[200px] max-w-xs">
          <label className="mb-1 block text-[11px] uppercase tracking-wider text-ink-3">
            {t('admin:financial.refunds.filters.source')}
          </label>
          <Select
            value={source}
            placeholder={t('admin:financial.refunds.filters.all')}
            options={REFUND_SOURCE_KINDS.map((s) => ({
              value: s,
              label: t(`admin:financial.refunds.source.${s}`, { defaultValue: s }),
            }))}
            onChange={(e) => {
              setSource(e.target.value as RefundSourceKind | '');
              setPage(1);
            }}
            data-testid="finance-refunds-source"
            aria-label={t('admin:financial.refunds.filters.source')}
          />
        </div>
        {source ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSource('');
              setPage(1);
            }}
            data-testid="finance-refunds-clear"
          >
            {t('admin:financial.refunds.filters.clear')}
          </Button>
        ) : null}
      </div>

      <DateRangePicker
        value={{ start: from, end: to }}
        onChange={(dr) => {
          setFrom(dr.start ?? from);
          setTo(dr.end ?? to);
          setPage(1);
        }}
        locale={locale}
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="finance-refunds-loading">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState title={t('admin:financial.refunds.empty')} />
      ) : (
        <>
          <Table
            data={rows as FinanceRefundRow[]}
            columns={columns as TableColumn<FinanceRefundRow>[]}
            rowKey={(row) => row.id}
            ariaLabel={t('admin:financial.refunds.title')}
          />
          {pageCount > 1 ? (
            <div className="flex justify-end">
              <Pagination
                page={page}
                pageCount={pageCount}
                onChange={setPage}
                ariaLabel={t('admin:financial.refunds.title')}
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export default FinanceRefundsPage;
